import React, { useEffect, useState, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { Layout, Menu, message, Button, Flex, Skeleton } from 'antd';
import * as AntdIcons from '@ant-design/icons';

import Dashboard from '../Dashboard/index';
import Planing from '../Planing/index';
import Evaluation from '../Evaluation/index';
import Faculties from '../Faculties/index.jsx';
import AdminPlanning from '../Admin/Planning/index.jsx';
import AdminEvaluation from '../Admin/Evaluation/index.jsx';
import Profile from '../Profile/index.jsx';

import { UserInfo } from '../../components/UserInfo/index';
import { BrandLogo, BrandHeadline } from '../../components/Brand';
import { confirmations } from './../../components/Confirmations';

import {
  layoutStruct,
  headerStruct,
  siderStruct,
  contentStruct,
  signOutButtonStruct
} from './struct.js';

import { getApa } from '../../actions/apa.action.js';
import { getStatic } from '../../actions/static.action.js';
import { selectMenu } from '../../actions/menu.js';
import { login, logout } from '../../actions/login.action.js';

import { IsFormSaved } from '../../context/FromStatusContext';

import auth from '../../services/auth.services.js';

import { menuList } from './const.js';
import FacultyDetails from '../Faculties/faculty-details/index.jsx';
import Results from '../Results/index.jsx';
import FacultyResults from '../Faculty-Result/index.jsx';
import PublicationDetails from '../Publication-Details/index.jsx';

const shouldHighlightPublication = true;

const { header, actions } = headerStruct;
const { Header, Sider, Content } = Layout;

const CustomLayout = ({ isDarkMode, handleTheme }) => {
  const { isFormSaved } = useContext(IsFormSaved);

  const [userInfo, setUserInfo] = useState(null);
  const [openKeys, setOpenKeys] = useState(['planing', 'planing-teaching']);

  const { status, formNo, id, formID } = useParams();
  const location = useLocation();

  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user);
  const { isLoggedIn, userDetails } = user;
  const apa = useSelector((state) => state.apa);
  const { apaLoading, apaData, apaError, apaErrorMsg } = apa;
  const _static = useSelector((state) => state.static);
  const { staticData } = _static;

  // ✅ menu is declared BEFORE the useEffect that uses it
  const menu = useSelector((state) => state.menu);

  const getItem = (label, key, icon, children) => ({
    key,
    icon,
    children,
    label
  });

  const items = [
    getItem('User Manual', '/home', <AntdIcons.PieChartOutlined />),
    getItem('Profile', '/profile', <AntdIcons.ProfileOutlined />),

    // Planning — blue variants
    getItem(
      <span style={{ color: '#185FA5', fontWeight: 600 }}>Planning</span>,
      'planing',
      <AntdIcons.EditOutlined style={{ color: '#185FA5' }} />,
      [
        getItem(
          <span style={{ color: '#0C447C', fontWeight: 600 }}>Teaching</span>,
          'planing-teaching',
          null,
          menuList.filter(i => i.id.startsWith('1.')).map(item =>
            getItem(
              <span style={{ color: '#185FA5' }}>{item.form_no} ~ {item.form_name}</span>,
              `/planing/${item.id}`,
              <AntdIcons.FileOutlined style={{ color: '#378ADD' }} />
            )
          )
        ),
        getItem(
          <span style={{ color: '#0C447C', fontWeight: 600 }}>Research</span>,
          'planing-research',
          null,
          menuList.filter(i => i.id.startsWith('2.')).map(item =>
            getItem(
              <span style={{ color: '#185FA5' }}>{item.form_no} ~ {item.form_name}</span>,
              `/planing/${item.id}`,
              <AntdIcons.FileOutlined style={{ color: '#378ADD' }} />
            )
          )
        ),
        getItem(
          <span style={{ color: '#0C447C', fontWeight: 600 }}>Service</span>,
          'planing-service',
          null,
          menuList.filter(i => i.id.startsWith('3.')).map(item =>
            getItem(
              <span style={{ color: '#185FA5' }}>{item.form_no} ~ {item.form_name}</span>,
              `/planing/${item.id}`,
              <AntdIcons.FileOutlined style={{ color: '#378ADD' }} />
            )
          )
        ),
      ]
    ),

    // Evaluation — green variants
    getItem(
      <span style={{ color: '#3B6D11', fontWeight: 600 }}>Evaluation</span>,
      'evaluation',
      <AntdIcons.CheckCircleOutlined style={{ color: '#3B6D11' }} />,
      [
        getItem(
          <span style={{ color: '#27500A', fontWeight: 600 }}>Teaching</span>,
          'evaluation-teaching',
          null,
          menuList.filter(i => i.id.startsWith('1.')).map(item =>
            getItem(
              <span style={{ color: '#3B6D11' }}>{item.form_no} ~ {item.form_name}</span>,
              `/evaluation/${item.id}`,
              <AntdIcons.FileOutlined style={{ color: '#639922' }} />
            )
          )
        ),
        getItem(
          <span style={{ color: '#27500A', fontWeight: 600 }}>Research</span>,
          'evaluation-research',
          null,
          menuList.filter(i => i.id.startsWith('2.')).map(item =>
            getItem(
              <span style={{ color: '#3B6D11' }}>{item.form_no} ~ {item.form_name}</span>,
              `/evaluation/${item.id}`,
              <AntdIcons.FileOutlined style={{ color: '#639922' }} />
            )
          )
        ),
        getItem(
          <span style={{ color: '#639922', fontWeight: 600 }}>Service</span>,
          'evaluation-service',
          null,
          menuList.filter(i => i.id.startsWith('3.')).map(item =>
            getItem(
              <span style={{ color: '#3B6D11' }}>{item.form_no} ~ {item.form_name}</span>,
              `/evaluation/${item.id}`,
              <AntdIcons.FileOutlined style={{ color: '#639922' }} />
            )
          )
        ),
      ]
    ),
    getItem(
      <span className={shouldHighlightPublication ? 'publication-menu-highlight' : ''}>
        Submit Publications Data
      </span>,
      '/submit-publications-data',
      <AntdIcons.FileOutlined
        style={{ color: '#ff0000' }}
        className={shouldHighlightPublication ? 'publication-menu-icon' : ''}
      />
    ),

    getItem('My Results', '/results', <AntdIcons.TrophyOutlined />),


    (userDetails?.role === 'dean' || userDetails?.role === 'admin' || userDetails?.role === 'vc' || userDetails?.role === 'hod') &&
    getItem('Faculty Results', '/faculty-results', <AntdIcons.BarChartOutlined />),

    (userDetails?.role === 'dean' || userDetails?.role === 'admin' || userDetails?.role === 'vc' || userDetails?.role === 'hod') &&
    getItem('Faculties', '/faculties', <AntdIcons.TeamOutlined />),
  ];

  const handleClickOnMenuItem = (e) =>
    isFormSaved
      ? navigate(`${e.key}`)
      : confirmations.forRouteChange(() => navigate(`${e.key}`));

  const fetchApa = () => dispatch(getApa());
  const fetchStatic = () => dispatch(getStatic());

  useEffect(() => {
    if (apaError && apaErrorMsg) messageApi.error(`${apaErrorMsg}`);
  }, [apaError]);

  useEffect(() => {
    const token = auth.retriveToken();
    const parsedUserInfo = JSON.parse(auth.retriveUserInfo());

    if (token) {
      const decoded_token = jwtDecode(token);
      dispatch(login(decoded_token));
      fetchApa();
      fetchStatic();
    } else window.location.href = '/';

    setUserInfo(parsedUserInfo);
  }, [auth.retriveUserInfo]);

  useEffect(() => {
    const selectedApas = apaData.filter((item) =>
      item.form_no.trim().startsWith(formNo?.trim())  // ✅ catches 1.1, 1.1a, 1.1b
    );
    const selectedApa = selectedApas.length ? selectedApas[0] : {};

    const selectedMenus = menuList.filter((item) =>
      item.id === formNo
    );
    const selectedMenu = selectedMenus.length ? selectedMenus[0] : {};

    dispatch(
      selectMenu({
        selectedApas: selectedApas,
        apaId: selectedApa.id,
        apaStatus: status,
        menuType: status,
        menuId: formNo,
        nextMenu: selectedMenu.next,
        prevMenu: selectedMenu.prev
      })
    );
  }, [formNo, location, JSON.stringify(apaData)]);

  // ✅ Now safe to use menu here — declared above
  useEffect(() => {
    if (!menu.currentMenuId || !menu.currentMenuType) return;

    const type = menu.currentMenuType; // 'planing' or 'evaluation'
    const id = menu.currentMenuId;

    const subKey = id.startsWith('1.') ? `${type}-teaching`
      : id.startsWith('2.') ? `${type}-research`
        : id.startsWith('3.') ? `${type}-service`
          : null;

    if (subKey) setOpenKeys([type, subKey]);
  }, [menu.currentMenuId, menu.currentMenuType]);

  let toRender = null;

  if (id && formID && location.pathname.includes('evaluation'))
    toRender = <AdminEvaluation key={`${id}/${formID}`} id={id} formID={formID} />;
  else if (id && formID && location.pathname.includes('planning'))
    toRender = <AdminPlanning key={`${id}/${formID}`} menu={menu} id={id} formID={formID} />;
  else if (id && location.pathname.startsWith('/faculty/'))
    toRender = <FacultyDetails key={id} />;
  else if (status === 'planing') toRender = <Planing menu={menu} />;
  else if (status === 'evaluation')
    toRender = <Evaluation menu={menu} />;
  else if (location?.pathname === '/home')
    toRender = <Dashboard menu={menu} />;
  else if (location?.pathname === '/profile')
    toRender = <Profile menu={menu} userInfo={userInfo} />;
  else if (location?.pathname === '/faculties')
    toRender = <Faculties menu={menu} />;
  else if (location?.pathname === '/results')
    toRender = <Results menu={menu} userInfo={userInfo} />;
  else if (location?.pathname === '/submit-publications-data')
    toRender = <PublicationDetails />;
  else if (location?.pathname === '/faculty-results')
    toRender = <FacultyResults />;
  else if (location?.pathname?.startsWith('/faculty'))
    toRender = <FacultyDetails />;

  return user && isLoggedIn ? (
    <Layout {...layoutStruct}>
      <Sider {...siderStruct}>
        <BrandLogo />
        <UserInfo user={userInfo} />

        {apaLoading ? (
          <Skeleton loading={apaLoading} active />
        ) : (
          <Menu
            selectedKeys={[`/${menu.currentMenuType}/${menu.currentMenuId}`]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            mode="inline"
            items={items}
            onClick={handleClickOnMenuItem}
          />
        )}
      </Sider>
      <Layout>
        <Header style={{ padding: " 10px 10px 0px" }}>
          <Flex {...header}>
            <BrandHeadline />
            <Flex {...actions}>
              <Button
                icon={
                  isDarkMode ? (
                    <AntdIcons.SunOutlined />
                  ) : (
                    <AntdIcons.MoonOutlined />
                  )
                }
                onClick={handleTheme}>
                Change Theme to {isDarkMode ? 'Light' : 'Dark'}
              </Button>
              <Button
                {...signOutButtonStruct}
                icon={<AntdIcons.PoweroffOutlined />}
                onClick={() => dispatch(logout())}
              >
                Sign Out
              </Button>
            </Flex>
          </Flex>
        </Header>
        {apaData && apaData.length && staticData ? (
          <Content {...contentStruct}>
            {contextHolder}
            {toRender}
          </Content>
        ) : (
          <></>
        )}
      </Layout>
    </Layout>
  ) : (
    <></>
  );
};

export default CustomLayout;